import { useState } from 'react';
import {
  Plus,
  Repeat,
  Pencil,
  Trash2,
  Pause,
  Play,
  Calendar,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { useRecurrences } from '@hooks/useRecurrences';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import RecurrenceForm from '@components/recurrences/RecurrenceForm';
import { formatCurrency, formatDate } from '@utils/formatters';
import { FREQUENCY_LABELS } from '@utils/constants';
import { clsx } from 'clsx';

const Recurrences = () => {
  const {
    incomeRecurrences,
    expenseRecurrences,
    totalIncome,
    totalExpense,
    balance,
    isLoading,
    createRecurrence,
    updateRecurrence,
    deleteRecurrence,
    pauseRecurrence,
    resumeRecurrence,
    isCreating,
    isUpdating,
  } = useRecurrences();

  const { accounts } = useAccounts();
  const { categories } = useCategories();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecurrence, setSelectedRecurrence] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleOpenModal = (recurrence = null) => {
    setSelectedRecurrence(recurrence);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedRecurrence(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (data) => {
    if (selectedRecurrence) {
      await updateRecurrence({ recurrenceId: selectedRecurrence.recurrenceId, data });
    } else {
      await createRecurrence(data);
    }
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (selectedRecurrence) {
      await deleteRecurrence(selectedRecurrence.recurrenceId);
      setIsDeleteModalOpen(false);
      setSelectedRecurrence(null);
    }
  };

  const handleTogglePause = async (recurrence) => {
    if (recurrence.isActive) {
      await pauseRecurrence(recurrence.recurrenceId);
    } else {
      await resumeRecurrence(recurrence.recurrenceId);
    }
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.accountId === accountId);
    return account ? `${account.icon} ${account.name}` : accountId;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.categoryId === categoryId);
    return category ? `${category.icon} ${category.name}` : categoryId;
  };

  if (isLoading) {
    return <Loading text="Carregando recorrências..." />;
  }

  const RecurrenceCard = ({ recurrence, type }) => (
    <div className={clsx(
      'flex items-center gap-3 p-3.5 rounded-xl border transition-all group',
      recurrence.isActive
        ? 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm'
        : 'border-gray-100 dark:border-gray-700 opacity-50'
    )}>
      <div className={clsx(
        'w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center',
        type === 'income' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'
      )}>
        <Repeat size={18} className={type === 'income' ? 'text-emerald-600' : 'text-red-600'} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
            {recurrence.description}
          </p>
          {!recurrence.isActive && <Badge variant="warning" size="sm">Pausada</Badge>}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-gray-400">{FREQUENCY_LABELS[recurrence.frequency]}</span>
          {recurrence.nextDueDate && (
            <>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <span className="text-xs text-gray-400 flex items-center gap-0.5">
                <Calendar size={10} />
                {formatDate(recurrence.nextDueDate)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <p className={clsx(
          'text-sm font-bold mr-1',
          type === 'income' ? 'text-emerald-600' : 'text-red-600'
        )}>
          {type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(recurrence.amount))}
        </p>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => handleTogglePause(recurrence)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={recurrence.isActive ? 'Pausar' : 'Retomar'}
          >
            {recurrence.isActive
              ? <Pause size={14} className="text-yellow-500" />
              : <Play size={14} className="text-emerald-500" />}
          </button>
          <button
            onClick={() => handleOpenModal(recurrence)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Pencil size={14} className="text-gray-400" />
          </button>
          <button
            onClick={() => { setSelectedRecurrence(recurrence); setIsDeleteModalOpen(true); }}
            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 size={14} className="text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );

  const hasRecurrences = incomeRecurrences.length > 0 || expenseRecurrences.length > 0;

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Recorrências"
        subtitle="Gerencie suas receitas e despesas fixas"
        action={
          <Button leftIcon={<Plus size={18} />} onClick={() => handleOpenModal()}>
            Nova Recorrência
          </Button>
        }
      />

      <PageTutorial
        pageId="recurrences"
        title="Lancamentos recorrentes"
        steps={[
          'Cadastre contas fixas como aluguel, salario, assinaturas, etc',
          'Defina a frequencia: diaria, semanal, mensal ou anual',
          'Recorrencias geram lancamentos automaticos na sua timeline de transacoes',
          'Voce pode pausar e retomar recorrencias a qualquer momento',
        ]}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Receitas Fixas</p>
            <div className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
              <TrendingUp size={16} className="text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalIncome)}</p>
          <p className="text-xs text-gray-400 mt-1">{incomeRecurrences.length} recorrência{incomeRecurrences.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Despesas Fixas</p>
            <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/30">
              <TrendingDown size={16} className="text-red-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(totalExpense)}</p>
          <p className="text-xs text-gray-400 mt-1">{expenseRecurrences.length} recorrência{expenseRecurrences.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Saldo Mensal Previsto</p>
            <div className={clsx('p-2 rounded-xl', balance >= 0 ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-red-100 dark:bg-red-900/30')}>
              {balance >= 0
                ? <ArrowUpRight size={16} className="text-primary-600" />
                : <ArrowDownRight size={16} className="text-red-600" />}
            </div>
          </div>
          <p className={clsx('text-2xl font-bold', balance >= 0 ? 'text-primary-600' : 'text-red-600')}>
            {formatCurrency(balance)}
          </p>
        </div>
      </div>

      {!hasRecurrences ? (
        <EmptyState
          icon={<Repeat size={32} />}
          title="Nenhuma recorrência cadastrada"
          description="Adicione suas receitas e despesas fixas para melhor controle financeiro."
          action={() => handleOpenModal()}
          actionLabel="Adicionar Recorrência"
        />
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Income */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Receitas Fixas</h3>
                <p className="text-xs text-gray-400 mt-0.5">{incomeRecurrences.length} recorrência{incomeRecurrences.length !== 1 ? 's' : ''}</p>
              </div>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1 rounded-full">
                +{formatCurrency(totalIncome)}/mês
              </span>
            </div>
            {incomeRecurrences.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">Nenhuma receita fixa cadastrada</p>
            ) : (
              <div className="space-y-2">
                {incomeRecurrences.map((r) => <RecurrenceCard key={r.recurrenceId} recurrence={r} type="income" />)}
              </div>
            )}
          </div>

          {/* Expense */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Despesas Fixas</h3>
                <p className="text-xs text-gray-400 mt-0.5">{expenseRecurrences.length} recorrência{expenseRecurrences.length !== 1 ? 's' : ''}</p>
              </div>
              <span className="text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-900/20 px-2.5 py-1 rounded-full">
                -{formatCurrency(totalExpense)}/mês
              </span>
            </div>
            {expenseRecurrences.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">Nenhuma despesa fixa cadastrada</p>
            ) : (
              <div className="space-y-2">
                {expenseRecurrences.map((r) => <RecurrenceCard key={r.recurrenceId} recurrence={r} type="expense" />)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedRecurrence ? 'Editar Recorrência' : 'Nova Recorrência'}
        size="md"
      >
        <RecurrenceForm
          recurrence={selectedRecurrence}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={isCreating || isUpdating}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Recorrência"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir a recorrência <strong>{selectedRecurrence?.description}</strong>?
          Transações já geradas não serão afetadas.
        </p>
      </Modal>
    </div>
  );
};

export default Recurrences;
